import { useState, useEffect } from 'react';
import { Button, DatePicker, Typography } from 'antd';
import moment from 'moment';
import dayjs from 'dayjs';
import axios from 'axios';
import { API_BASE_URL } from '../../../../../api';
import { useSelector } from "react-redux";
import Sidebar from "../../GlobalFiles/Sidebar";
import { toast } from "react-toastify";
import exportCollectionToExcel from '../../../../../helper/exportCollectionToXLS';
import printDailyCollectionShort from '../../../../../helper/printDailyCollectionShort';

const { Title } = Typography;

const DailyCollectionShort = () => {
    const [loading, setLoading] = useState(false);
    const [collectionData, setCollectionData] = useState(null);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [selectedDateString, setSelectedDateString] = useState(dayjs().format('YYYY-MM-DD'));

    const { data: { user }, } = useSelector((state) => state.auth);
    const isAdmin = user?.userType === "admin";

    useEffect(() => {
        fetchDailyCollection(moment().format('YYYY-MM-DD'));
    }, []);

    const fetchDailyCollection = async (dateOverride = null) => {
        const activeDateString = dateOverride || selectedDateString;
        const activeDate = activeDateString ? null : selectedDate;
        if (!activeDateString && !activeDate) return;
        setLoading(true);
        try { 
            const dateMoment = activeDate
                ? (moment.isMoment(activeDate) ? activeDate.clone() : moment(activeDate))
                : null;
            const localDate = dateMoment ? dateMoment.format('YYYY-MM-DD HH:mm:ss') : 'N/A';
            const utcDate = dateMoment ? dateMoment.clone().utc().format('YYYY-MM-DD HH:mm:ss') : 'N/A';
            const nowLocal = moment().format('YYYY-MM-DD HH:mm:ss');
            const nowUtc = moment().utc().format('YYYY-MM-DD HH:mm:ss');
            console.log(
                'Daily collection short date (local):',
                localDate,
                'UTC:',
                utcDate,
                'ISO:',
                dateMoment ? dateMoment.toISOString() : 'N/A',
                'Now local:',
                nowLocal,
                'Now UTC:',
                nowUtc
            );
            const dateKey = activeDateString || (dateMoment ? dateMoment.format('YYYY-MM-DD') : "");
            const rangeStartUtc = moment(dateKey, 'YYYY-MM-DD').startOf('day').utc().toISOString();
            const rangeEndUtc = moment(dateKey, 'YYYY-MM-DD').endOf('day').utc().toISOString();
            console.log('Fetching daily collection short range (UTC):', rangeStartUtc, rangeEndUtc);
            const response = await axios.get(`${API_BASE_URL}/testorders/collection`, {
                params: { startUtc: rangeStartUtc, endUtc: rangeEndUtc },
            });
            console.log('Daily collection response:', response.data?.orders?.[0]);
            setCollectionData(response.data);
        } catch (error) {
            console.error('Error fetching daily collection:', error);
            console.error('Error response:', error.response?.data);
            toast.error('Failed to fetch daily collection data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDailyCollection();
    }, [selectedDate, selectedDateString]);


    const exportToExcel = () => {
        if (!collectionData) return;
        const sgs = collectionData.superGroups || [];
        exportCollectionToExcel(sgs, collectionData.orders, { mode: 'daily-short' });
    };

    const handlePrint = () => {
        if (!collectionData) return;
        const sgs = collectionData.superGroups || [];
        printDailyCollectionShort(collectionData.orders);
    };

    useEffect(() => {
        console.log("Collection data updated:", collectionData);
    }, [collectionData]);

    const orders = collectionData?.orders || [];
    const daysInMonth = selectedDate?.toDate().getDate() || 0;
    const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

    const getTotalOfDay = (type, day) => {
        console.log("Calculating total of day:", { type, day });

        if (!type) {
            console.log("Invalid type or day in getTotalOfDay");
            return 0;
        };
        if (!collectionData) return 0;

        let total = 0;
        collectionData.orders.forEach(order => {
            const orderDate = new Date(order.createdAt);
            if (day && orderDate.getDate() !== day) return;
            if (type === 'paid') total += order.realPaidAmount || 0;
            if (type === 'due') total += order.realDueAmount || 0;
            if (type === 'total') total += order.realTotalAmount || 0;
            if (type === 'less') total += order.realDiscountAmount || 0;
        });
        console.log("Total for day:", total);
        return total;
    };

    if (!isAdmin) {
        return (
            <div className="flex min-h-screen bg-gray-100">
                <Sidebar />
                <div className="flex-1 p-4 md:p-8 flex items-center justify-center">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
                        <p className="text-gray-600">Only managers can access this page.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="flex min-h-screen bg-gray-50 overflow-x-hidden">
                <Sidebar onCollapse={setSidebarCollapsed} />
                <div className={`flex-1 min-w-0 p-6 transition-all duration-300 ${sidebarCollapsed ? 'ml-15' : 'ml-0'}`}>
                    <div className="flex-1 min-w-0 p-6 ml-20">
                        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-3 gap-4 bg-gradient-to-r from-sky-600 to-indigo-500 p-6 rounded-lg">
                            <h1 className="text-3xl font-bold text-gray-200 flex items-center">
                                <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                Daily Collection Report
                            </h1>
                            <div className="flex flex-col md:flex-row md:items-center gap-2 md:space-x-4">
                                <Button
                                    type="primary"
                                    onClick={() => fetchDailyCollection()}
                                    className="px-4 py-2 bg-gray-100 text-black rounded-md hover:bg-gray-300 transition-colors flex items-center whitespace-nowrap"
                                >
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    Refresh
                                </Button>
                                <DatePicker
                                    onChange={(date, dateString) => {
                                        if (date) {
                                            setSelectedDate(date);
                                            setSelectedDateString(dateString || "");
                                            fetchDailyCollection(dateString || null);
                                        }
                                    }}
                                    value={selectedDate ? dayjs(selectedDate) : null}
                                    className="w-full md:w-auto"
                                    placeholder="Select Date"
                                    allowClear={false}
                                />
                                <button
                                    onClick={handlePrint}
                                    disabled={!collectionData}
                                    aria-label="Print report"
                                    className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors flex items-center disabled:opacity-60 whitespace-nowrap"
                                >
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                                    </svg>
                                    Print Report
                                </button>
                                <button
                                    onClick={exportToExcel}
                                    disabled={true}
                                    aria-label="Export to Excel"
                                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center disabled:opacity-60 whitespace-nowrap disabled:pointer-events-none"
                                >
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                    Export to Excel
                                </button>
                            </div>
                        </div>

                        <Title level={4} className="m-0">Selection: {selectedDate !== null ? selectedDate.toDate().toDateString() : 'No date selected'}</Title>

                        {loading && (
                            <div className="flex min-h-screen bg-gray-50">
                                <Sidebar onCollapse={setSidebarCollapsed} />
                                <div className={`flex-1 p-6 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0'}`}>
                                    <div className="flex-1 p-6 ml-40">
                                        <div className="text-center p-12">
                                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
                                            <p className="mt-4 text-gray-600">Loading daily collection data...</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {(!collectionData) && (
                            (!loading && !selectedDate) ? (
                                <div className="flex min-h-screen bg-gray-50">
                                    <Sidebar onCollapse={setSidebarCollapsed} />
                                    <div className={`flex-1 p-6 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0'}`}>
                                        <div className="flex-1 p-6 ml-40">
                                            <div className="text-center p-12">
                                                <h2 className="text-2xl font-bold text-gray-900 mb-2">Date not selected</h2>
                                                <p className="text-gray-600">Please Select a date to see collections.</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex min-h-screen bg-gray-50">
                                    <Sidebar onCollapse={setSidebarCollapsed} />
                                    <div className={`flex-1 p-6 transition-all duration-300 ${sidebarCollapsed ? 'ml-20' : 'ml-0'}`}>
                                        <div className="flex-1 p-6 ml-40">
                                            <div className="text-center p-12">
                                                <h2 className="text-2xl font-bold text-gray-900 mb-2">No Data Available</h2>
                                                <p className="text-gray-600">No collection data found for the selected date.</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )
                        )}

                        {orders.length > 0 && (
                            <div className="bg-white rounded-lg shadow-md p-6">
                                {/* Summary Cards */}
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                                    <div className="bg-blue-50 p-4 rounded-lg">
                                        <div className="flex items-center">
                                            <svg className="w-8 h-8 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                            <div>
                                                <p className="text-sm font-medium text-blue-600">Total Orders</p>
                                                <p className="text-2xl font-bold text-blue-900">{orders?.length || 0}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-emerald-50 p-4 rounded-lg">
                                        <div className="flex items-center">
                                            <svg className="w-8 h-8 text-emerald-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                            </svg>
                                            <div>
                                                <p className="text-sm font-medium text-emerald-600">Total Paid</p>
                                                <p className="text-2xl font-bold text-emerald-900">{orders?.filter(item => item.isPaid).length || 0}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-red-50 p-4 rounded-lg">
                                        <div className="flex items-center">
                                            <svg className="w-8 h-8 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                            </svg>
                                            <div>
                                                <p className="text-sm font-medium text-red-600">Total Due</p>
                                                <p className="text-2xl font-bold text-red-900">{collectionData.orders?.filter(item => !item.isPaid).length || 0}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-8 w-full overflow-x-auto">
                                    <div className='flex justify-between items-center my-3'>
                                        <h3 className="text-lg font-semibold text-gray-900">Orders Details</h3>
                                    </div>
                                    <div className="overflow-x-auto">
                                        <table className="w-full min-w-[900px] table-auto border-collapse">
                                            <thead>
                                                <tr className="bg-sky-200 text-sky-900 font-semibold">
                                                    {/* <th className="p-3 text-left border-b whitespace-nowrap  min-w-20">Order ID</th> */}
                                                    <th className="p-3 text-left border-b whitespace-nowrap">Advance</th>
                                                    <th className="p-3 text-left border-b whitespace-nowrap">Due</th>
                                                    <th className="p-3 text-left border-b whitespace-nowrap">Less</th>
                                                    <th className="p-3 text-left border-b whitespace-nowrap">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {/* Loop over the days of selected date */}
                                                {orders.map((order, index) => (
                                                    <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-200/80"}>
                                                        {/* <td className="p-3 border-b border-gray-200 whitespace-nowrap"> {order.testOrderID}</td> */}
                                                        <td className="p-3 border-b border-gray-200 whitespace-nowrap">৳{(order.realPaidAmount || 0).toFixed(2)}</td>
                                                        <td className="p-3 border-b border-gray-200 whitespace-nowrap">৳{(order.realDueAmount || 0).toFixed(2)}</td>
                                                        <td className="p-3 border-b border-gray-200 whitespace-nowrap">৳{(order.realDiscountAmount || 0).toFixed(2)}</td>
                                                        <td className="p-3 border-b border-gray-200 whitespace-nowrap">৳{(order.realTotalAmount || 0).toFixed(2)}</td>
                                                    </tr>
                                                ))}

                                                {(!collectionData.orders || collectionData.orders.length === 0) && (
                                                    <tr>
                                                        <td className="p-3 py-6 border-b border-gray-200 text-center font-bold" colSpan="12">No orders found for the selected date.</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                            <tfoot>
                                                <tr className="bg-slate-100 text-slate-900 font-bold">
                                                    <td className="p-3">৳{orders.reduce((acc, order) => acc + (order.realPaidAmount || 0), 0).toFixed(2)}</td>
                                                    <td className="p-3">৳{orders.reduce((acc, order) => acc + (order.realDueAmount || 0), 0).toFixed(2)}</td>
                                                    <td className="p-3">৳{orders.reduce((acc, order) => acc + (order.realDiscountAmount || 0), 0).toFixed(2)}</td>
                                                    <td className="p-3">৳{orders.reduce((acc, order) => acc + (order.realTotalAmount || 0), 0).toFixed(2)}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default DailyCollectionShort;
